import {
	CloseOutlined,
	DownOutlined,
	LeftOutlined,
	MessageFilled,
} from "@ant-design/icons"
import { Avatar } from "components/Avatar/Avatar"
import { Badge } from "components/Badge/Badge"
import { Button, type ButtonProps } from "components/Button/Button"
import { Dropdown } from "components/Dropdown/Dropdown"
import { ErrorStatus } from "components/ErrorStatus/ErrorStatus"
import { Layout } from "components/Layout/Layout"
import { Modal } from "components/Modal/Modal"
import { Space } from "components/Space/Space"
import { Tooltip } from "components/Tooltip/Tooltip"
import { Wrap } from "components/Wrap/Wrap"
import { useDisclosure } from "hooks/useDisclosure"
import { useElement } from "hooks/useElement"
import { useModalNavigate } from "hooks/useModalNavigate"
import { ChatMessage, MessageType, Participant } from "interface/communications"
import { Patient } from "interface/patientType"
import {
	ComponentProps,
	KeyboardEventHandler,
	MouseEventHandler,
	useCallback,
	useMemo,
} from "react"
import { createPortal } from "react-dom"
import { useLocation, useNavigate, useParams } from "react-router"
import {
	useFindLatestChatMessages,
	useMarkAllMessagesAsRead,
	useUnreadMessagesCount,
} from "services/communications"
import { usePatientDetails } from "services/patients"
import { useOrganization, useUserRole } from "services/users"
import { cn } from "styles/cn"
import { asDate, getChatMessageDisplayTime } from "utils/date"
import { sortByFullName } from "utils/patient"
import { ChatInput } from "./ChatInput"
import { CallStatusMessage } from "./ChatItems"
import { ChatList } from "./ChatList"
import { ChatContextType, ChatProvider } from "./ChatProvider"
import { Voice } from "./Voice"

export const Chat = Object.assign({}, { CallToAction, Window })

function CallToAction({
	view,
	send,
	patient,
	unreadMessagesCount = 0,
	disabled,
	onlyIcon = false,
	type = "primary",
	size,
	className,
}: Pick<ButtonProps, "type" | "size" | "className" | "disabled"> & {
	view: boolean
	send: boolean
	patient: Patient
	unreadMessagesCount?: number
	onlyIcon?: boolean
}) {
	const modalNavigate = useModalNavigate()
	const location = useLocation()
	const buttonLabel = "Messages"
	const participants = useMemo(() => {
		if (!patient) {
			return []
		}
		const participants: Partial<Participant>[] = [
			{
				id: patient.id,
				fullName: patient.fullName,
				displayName: patient.fullName,
			},
		]

		if (patient.alternateContacts) {
			participants.push(
				...patient.alternateContacts
					.toSorted(sortByFullName)
					.map(({ _id: id, fullName, relationship }) => ({
						id,
						fullName,
						displayName: `${fullName} (${relationship})`,
					})),
			)
		}
		return participants
	}, [patient])
	const isSingle =
		participants.length === 1 || (onlyIcon && participants.length > 0)
	const isMultiple = !onlyIcon && participants.length > 1
	const handleModalNavigate = useCallback(
		(participantId = participants[0].id) => {
			const to = `${location.pathname}/patients/${patient.id}/m/chat/${participantId}${location.search}`
			modalNavigate(to)
		},
		[
			location.pathname,
			location.search,
			modalNavigate,
			participants,
			patient.id,
		],
	)

	if ((!view && !send) || participants.length === 0) {
		return null
	}

	return (
		<Wrap
			condition={isSingle || isMultiple}
			with={(children) => (
				<Badge
					showZero={false}
					count={disabled ? 0 : unreadMessagesCount}
					classNames={{ indicator: "bg-blue-60", root: className }}
					onClick={(e) => e.stopPropagation()}
				>
					{children}
				</Badge>
			)}
		>
			<Wrap
				condition={onlyIcon}
				with={(children) => <Tooltip title={buttonLabel}>{children}</Tooltip>}
			>
				{isSingle && (
					<Button
						type={type}
						size={size}
						className={cn("w-full", className)}
						shape={onlyIcon ? "circle" : "default"}
						icon={<MessageFilled aria-hidden />}
						disabled={disabled}
						onClick={(e) => {
							e.stopPropagation()
							handleModalNavigate()
						}}
						aria-label={buttonLabel}
						onKeyDown={(e) => e.stopPropagation()}
					>
						{!onlyIcon && buttonLabel}
					</Button>
				)}

				{isMultiple && (
					<Space.Compact size={size} className={className}>
						<Button
							type={type}
							className={className}
							disabled={disabled}
							icon={<MessageFilled aria-hidden />}
							onClick={(e) => {
								e.stopPropagation()
								handleModalNavigate()
							}}
						>
							{buttonLabel}
						</Button>
						<Dropdown
							trigger={["click"]}
							placement="bottomRight"
							menu={{
								"aria-label": "Available contacts to message",
								items: participants.map(({ id, displayName }) => ({
									key: id!,
									label: displayName,
								})),
								onClick: ({ key }) => {
									handleModalNavigate(key)
								},
								onKeyDown: (e) => e.stopPropagation(),
							}}
						>
							<Button
								type={type}
								disabled={disabled}
								icon={<DownOutlined aria-hidden />}
								aria-label="Select a contact to message"
							/>
						</Dropdown>
					</Space.Compact>
				)}
			</Wrap>
		</Wrap>
	)
}

function Window() {
	const location = useLocation()
	const navigate = useNavigate()
	const { userId: patientId, activeParticipantId } = useParams()
	const discloseParticipants = useDisclosure()
	const disclosePop = useDisclosure()
	const containerElement = useElement("layout-content")
	const { data: provider, isFetched } = useUserRole()
	const organization = useOrganization()
	const send = organization !== undefined ? organization.hasSend : true
	const { data: patient } = usePatientDetails(patientId)
	const unreadMessagesCount = useUnreadMessagesCount(patientId)
	const hasUnreadMessages = (unreadMessagesCount.data ?? 0) > 0
	const participants = useMemo<Participant[]>(() => {
		if (!patient) {
			return []
		}

		const participants: Participant[] = [
			{
				id: patient.id,
				fullName: patient.fullName,
				displayName: patient.fullName,
				readOnly:
					!patient.phoneNumber?.supportsTextMessages &&
					!patient.hasEmailAddress,
				isActive: true,
				hasEmailAddress: patient.hasEmailAddress,
				supportsTextMessages: !!patient.phoneNumber?.supportsTextMessages,
				contactType: "User",
				userType: "Patient",
			},
		]

		if (patient.alternateContacts) {
			participants.push(
				...patient.alternateContacts.map<Participant>(
					({ _id: id, fullName, relationship, phoneNumber, isActive }) => ({
						id,
						fullName: `${fullName} (${relationship})`,
						displayName: `${fullName} (${relationship})`,
						readOnly: !phoneNumber.supportsTextMessages,
						isActive,
						hasEmailAddress: false,
						supportsTextMessages: phoneNumber.supportsTextMessages,
						contactType: "AlternateContact",
					}),
				),
			)
		}
		return participants
	}, [patient])
	const hasMultipleParticipants = participants.length > 1
	const activeParticipant = useMemo(
		() => participants.find(({ id }) => id === activeParticipantId),
		[activeParticipantId, participants],
	)
	const isReady = isFetched && activeParticipant
	const chatProviderValue = useMemo<Partial<ChatContextType>>(
		() => ({ patient, provider, sender: provider, activeParticipant }),
		[activeParticipant, patient, provider],
	)
	const {
		mutateAsync: markAllAsRead,
		isPending: markAsReadPending,
		isError: markAsReadIsError,
		error: markAsReadError,
	} = useMarkAllMessagesAsRead(patientId)
	const { data: latestMessages = [] } = useFindLatestChatMessages(patientId)
	const latestMessageId = useMemo(() => {
		const latestMessage = latestMessages.findLast(({ sender, receiver }) =>
			[sender, receiver].includes(activeParticipantId),
		)
		return latestMessage?.id
	}, [activeParticipantId, latestMessages])
	const chatInputProps = useMemo<
		Partial<ComponentProps<typeof ChatInput>>
	>(() => {
		if (!send) {
			return {
				disabled: true,
				placeholder: "No permissions to send new text messages",
			}
		} else if (!activeParticipant?.isActive) {
			return {
				disabled: true,
				placeholder: `${activeParticipant?.fullName}'s contact is archived`,
			}
		} else if (!!activeParticipant?.readOnly) {
			return {
				disabled: true,
				placeholder: `Text messaging is not enabled for ${activeParticipant.fullName}'s current phone number`,
			}
		}
		return {
			"aria-label": `Send a new text message to ${activeParticipant?.fullName}`,
		}
	}, [
		activeParticipant?.fullName,
		activeParticipant?.isActive,
		activeParticipant?.readOnly,
		send,
	])
	const handleOpenParticipant = useCallback(
		(participantId: string) => {
			const backgroundLocation = location.state?.backgroundLocation
			const to = `${backgroundLocation.pathname}/patients/${patientId}/m/chat/${participantId}`
			navigate(to, {
				state: { backgroundLocation },
			})
		},
		[location.state?.backgroundLocation, navigate, patientId],
	)
	const handleClose = useCallback(() => {
		const backgroundLocation = (location.state as any)?.backgroundLocation
		if (backgroundLocation) {
			navigate(backgroundLocation.pathname + backgroundLocation.search)
		} else {
			const chatPath = `/patients/${patientId}/m/chat/${activeParticipantId}`
			navigate(location.pathname.replace(chatPath, "") + location.search)
		}
	}, [
		activeParticipantId,
		location.pathname,
		location.search,
		location.state,
		navigate,
		patientId,
	])
	const handleCancel = useCallback(async () => {
		if (!latestMessageId) {
			return
		}
		try {
			await markAllAsRead(latestMessageId)
			disclosePop.close()
			handleClose()
		} catch (_) {}
	}, [disclosePop, handleClose, latestMessageId, markAllAsRead])

	if (
		!isReady ||
		!provider ||
		!patient ||
		!activeParticipantId ||
		!activeParticipant ||
		!containerElement
	) {
		return null
	}

	return createPortal(
		<>
			<div className="absolute inset-0 z-20 bg-black/50" />
			<div
				role="dialog"
				aria-label="Chat window"
				className={cn(
					"absolute inset-0 z-20 m-auto flex flex-col",
					"divide-x divide-grey-20 overflow-hidden",
					"@4xl/content:rounded-xl @4xl/content:border @4xl/content:border-grey-20",
					"max-h-full min-w-full",
					"@4xl/content:max-h-[calc(100%-(--spacing(30)))] @4xl/content:max-w-4xl @4xl/content:min-w-0",
					hasMultipleParticipants && "flex-row",
				)}
			>
				{hasMultipleParticipants && (
					<Layout.Sider
						theme="light"
						breakpoint="xs"
						collapsedWidth={0}
						trigger={null}
						width={280}
						role="none"
						className="hidden @4xl/content:block"
					>
						<Participants
							patientId={patient.id}
							participants={participants}
							participantId={activeParticipantId}
							onClick={handleOpenParticipant}
						/>
					</Layout.Sider>
				)}

				<Layout className="min-w-0">
					<Layout.Header
						className="flex items-center justify-start gap-2 bg-white p-4 text-lg font-normal"
						aria-label={`Chatting with ${activeParticipant.fullName}`}
					>
						{hasMultipleParticipants && (
							<Button
								type="text"
								className="@4xl/content:hidden"
								aria-label="All contacts menu"
								icon={<LeftOutlined aria-hidden />}
								onClick={discloseParticipants.toggle}
							/>
						)}
						<div className="w-fit">
							<Avatar className="size-8 self-start lg:size-8 lg:self-center">
								{activeParticipant.fullName}
							</Avatar>
						</div>
						<div className="flex min-w-0 items-center gap-2 font-headings">
							<p className="truncate">{activeParticipant?.fullName}</p>
							<Voice.CallToAction
								size="small"
								show={send}
								patient={patient}
								forceParticipantId={activeParticipantId}
							/>
						</div>
						<Button
							type="text"
							icon={<CloseOutlined aria-hidden />}
							aria-label="Close chat window"
							className="ml-auto text-navy-60"
							{...(hasUnreadMessages
								? { onClick: disclosePop.open }
								: { onClick: handleClose })}
						/>
						<Modal
							open={disclosePop.isOpen}
							title="You have unresponded messages"
							okText="Leave as unread"
							onOk={handleClose}
							cancelText="Mark as read"
							cancelButtonProps={{
								loading: markAsReadPending,
								disabled: !latestMessageId,
							}}
							onCancel={handleCancel}
							classNames={{ body: "flex flex-col gap-2" }}
						>
							{`There are ${unreadMessagesCount.data} messages that have not yet been replied
								to. Do you want to leave these messages as read before closing,
								or mark them as unread?`}
							<ErrorStatus show={markAsReadIsError}>
								{`${markAsReadError?.message}. Please, try again.`}
								<br />
							</ErrorStatus>
						</Modal>
					</Layout.Header>
					<ChatProvider defaultValue={chatProviderValue}>
						{({ threadId, toggleThread }) => (
							<>
								{threadId && (
									<div className="border-t border-grey-20 px-5 py-2">
										<Button
											icon={<LeftOutlined aria-hidden />}
											onClick={() => toggleThread(null)}
										>
											Return to feed
										</Button>
									</div>
								)}
								<ChatList
									className={cn(!threadId && "grow border-t border-grey-20")}
								/>
								{!threadId && (
									<ChatInput
										aria-label={chatInputProps["aria-label"]}
										disabled={chatInputProps.disabled}
										placeholder={chatInputProps.placeholder}
									/>
								)}
							</>
						)}
					</ChatProvider>
				</Layout>

				{hasMultipleParticipants && discloseParticipants.isOpen && (
					<div className="absolute size-full bg-white">
						<Participants
							title="All contacts menu"
							patientId={patient.id}
							participants={participants}
							participantId={activeParticipantId}
							onClick={(id) => {
								handleOpenParticipant(id)
								discloseParticipants.toggle()
							}}
						/>
					</div>
				)}
			</div>
		</>,
		containerElement,
	)
}

function Participants({
	title = "All contacts",
	patientId,
	participants,
	participantId,
	onClick,
}: {
	title?: string
	patientId: string
	participants: Participant[]
	participantId: string
	onClick: (id: string) => void
}) {
	const { data: latestMessages = [] } = useFindLatestChatMessages(patientId)
	const participantsWithLatestMessage = useMemo(
		() =>
			participants
				.map((participant) => ({
					...participant,
					latestMessage: getLatestMessage(latestMessages, participant.id!),
					hasUnreadMessages: includesUnreadMessages(
						latestMessages,
						participant.id!,
					),
				}))
				.toSorted((participantA, participantB) => {
					if (
						(!participantA.latestMessage && !participantB.latestMessage) ||
						[participantA.id, participantB.id].includes(patientId)
					) {
						return 0
					}
					if (!participantA.latestMessage) {
						return 1
					}
					if (!participantB.latestMessage) {
						return -1
					}
					return asDate(participantB.latestMessage.timestamp).diff(
						asDate(participantA.latestMessage.timestamp),
					)
				}),
		[latestMessages, participants, patientId],
	)
	return (
		<aside className="flex flex-col divide-y divide-grey-20" title={title}>
			<h2 className="px-4 py-5 font-headings text-lg font-extrabold">
				{title}
			</h2>
			<ul
				aria-label={`${participantsWithLatestMessage.length} available contacts`}
			>
				{participantsWithLatestMessage.map((participant) => {
					const {
						id,
						displayName,
						latestMessage,
						hasUnreadMessages,
						readOnly,
						isActive,
					} = participant
					const isPatient = id === patientId
					const isCurrent = participantId === id
					const ariaLabel = isCurrent
						? `Message thread with ${displayName} is active`
						: `Open message thread with ${displayName}`
					const handleClick: MouseEventHandler<HTMLLIElement> = () => {
						onClick(id!)
					}
					const handleKeyDown: KeyboardEventHandler<HTMLLIElement> = (e) => {
						if (e.key === "Enter" || e.key === " ") {
							onClick(id!)
						}
					}

					return (
						<li
							key={id}
							tabIndex={0}
							aria-label={ariaLabel}
							title={displayName}
							className={cn(
								"focus-outline relative flex min-h-16 items-center gap-2 p-3",
								isCurrent && "bg-grey-10",
								!isCurrent && "hover:cursor-pointer hover:bg-grey-30",
								isPatient && "border-b border-grey-10",
							)}
							onClick={handleClick}
							onKeyDown={handleKeyDown}
						>
							<div className="w-fit">
								<Avatar className="size-8 lg:size-8">{displayName}</Avatar>
							</div>
							<div className="w-full min-w-0 text-sm">
								<div className="flex items-center justify-between gap-1">
									<p className="truncate">{displayName}</p>
									{!!latestMessage?.timestamp && (
										<time
											dateTime={latestMessage.timestamp}
											className="whitespace-nowrap text-navy-60"
										>
											{getChatMessageDisplayTime(
												asDate(latestMessage.timestamp),
												false,
											)}
										</time>
									)}
								</div>
								<div className="flex items-center justify-between gap-1">
									<MessagePreview message={latestMessage} />
									{(readOnly || !isActive) && (
										<p className="whitespace-nowrap text-navy-60">
											{readOnly ? "Read-only" : !isActive ? "Archived" : ""}
										</p>
									)}
								</div>
							</div>
							{hasUnreadMessages && (
								<div className="absolute top-1 right-1 size-1.5 rounded-full bg-blue-60" />
							)}
						</li>
					)
				})}
			</ul>
		</aside>
	)
}

function MessagePreview({ message }: { message?: ChatMessage }) {
	const preview = (() => {
		if (!message) {
			return null
		}
		if (message.type === MessageType.voice) {
			return <CallStatusMessage message={message} />
		} else if (message.type === MessageType.voicemail) {
			return "(Voicemail)"
		} else if (!!message.mediaUrl) {
			return `(Media file)${message.text ? ` ${message.text}` : ""}`
		} else if (!!message.text) {
			return message.text
		}
		return null
	})()

	if (!preview) {
		return null
	}

	return <p className="truncate text-navy-60">{preview}</p>
}

function getLatestMessage(messages: ChatMessage[], id: string) {
	return messages.findLast(({ sender, receiver }) =>
		[sender, receiver].includes(id),
	)
}

function includesUnreadMessages(messages: ChatMessage[], id: string) {
	return messages.some(({ sender, isRead }) => sender === id && !isRead)
}
